'use client';

import { generateServiceLocationUrl } from '@/routes';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { ArrowRightIcon, CalendarIcon, ClockIcon, ShieldCheckIcon, WalletIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { BlogStickyBarConfigurationStoryblok } from '@/storyblok/types';

export const PostTopBar: React.FC<{ config?: BlogStickyBarConfigurationStoryblok; revealAfterId?: string }> = ({
  config,
  revealAfterId,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isRevealed, setIsRevealed] = useState(!revealAfterId);
  const [hasEntered, setHasEntered] = useState(false);
  const [hasSettled, setHasSettled] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!isRevealed) return;
    const frame = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [isRevealed]);

  useEffect(() => {
    if (!revealAfterId || isRevealed) return;

    let frame = 0;
    let observer: IntersectionObserver | undefined;

    const observeWhenReady = () => {
      // The index is rendered by a sibling server component, so it may not be in the DOM
      // the instant this effect runs (e.g. soft navigation). Wait for it instead of revealing.
      const target = document.getElementById(revealAfterId);
      if (!target) {
        frame = requestAnimationFrame(observeWhenReady);
        return;
      }

      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsRevealed(true);
          observer?.disconnect();
        }
      });
      observer.observe(target);
    };

    observeWhenReady();

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [revealAfterId, isRevealed]);

  if (!isOpen || config?.hide || !isRevealed) return null;

  return (
    <div
      className={`sticky inset-x-0 top-[calc(var(--header-top-offset,0px)+var(--header-height,0px))] z-50 border-b border-neutral-surface/50 bg-white px-4 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] xl:px-20 ${
        !hasSettled &&
        `transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none ${hasEntered ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`
      }`}
      onTransitionEnd={event => {
        if (event.target === event.currentTarget) setHasSettled(true);
      }}
    >
      <div className="py-3 sm:py-3.5 xl:mx-auto xl:max-w-screen-xl">
        <div className="flex items-center gap-3 sm:gap-5">
          <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-active/10 text-primary-active sm:flex">
            <CalendarIcon className="size-5 stroke-2" />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-y-1">
            <div className="truncate text-sm font-bold leading-tight text-neutral-darker sm:text-base">Hai bisogno di un consulto?</div>
            <div className="flex hidden flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-lighter sm:flex">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheckIcon className="size-4 shrink-0 stroke-2 text-primary" />
                <span>Specialisti verificati</span>
              </span>

              <span className="inline-flex items-center gap-1.5">
                <ClockIcon className="size-4 shrink-0 stroke-2 text-primary" />
                <span>Disponibilità rapide</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <WalletIcon className="size-4 shrink-0 stroke-2 text-primary" />
                <span>Prezzi trasparenti</span>
              </span>
            </div>

            <div className="truncate text-xs text-neutral-darker sm:hidden">Specialisti verificati · Disponibilità rapide</div>
          </div>

          <ELButton
            label="Prenota una visita"
            variant="floating"
            color="primary"
            trailingIcon={<ArrowRightIcon className="size-4 stroke-2" />}
            size="small"
            className="text-primary-white h-11 w-auto shrink-0 rounded-full bg-primary px-5 text-sm text-white transition-all hover:bg-primary-hover [&>div]:gap-2 [&_span]:font-semibold"
            onClick={() =>
              router.push(
                generateServiceLocationUrl(
                  'treatment',
                  config?.serviceValue && config.locationValue
                    ? { service: config.serviceValue, location: config.locationValue }
                    : { service: config?.serviceValue },
                ),
              )
            }
          />

          <button
            type="button"
            aria-label="Chiudi"
            className="hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-neutral-darker transition-colors hover:bg-primary/5 sm:size-9"
            onClick={() => setIsOpen(false)}
          >
            <XMarkIcon className="size-4 stroke-2" />
          </button>
        </div>
      </div>
    </div>
  );
};
